import * as React from 'react';
import {
  type ColumnDef,
  type ColumnSizingState,
  type ExpandedState,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, ChevronRight, ChevronsUpDown, ChevronUp, Download, FoldVertical, GripVertical, Info, Keyboard, Loader2, RotateCcw, Search, UnfoldVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { DataTablePager } from './data-table-pager';
import {
  buildCsv,
  type CsvExtraColumn,
  downloadCsv,
  getAlignmentClassName,
  getColumnMeta,
  getColumnWidth,
  getRowAccessibleLabel,
  getStickyClassName,
  headerToText,
  isSyntheticColumn,
  loadColumnOrder,
  loadColumnSizing,
  minColumnWidth,
  persistColumnOrder,
  persistColumnSizing,
  sanitizeColumnOrder,
  toCssSize,
  type DataTableDensity,
} from './data-table-utils';

export interface DataTableServer<TData> {
  /** Total rows across every page (used for page math + the pager readout). */
  rowCount: number;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  globalFilter?: string;
  onGlobalFilterChange?: OnChangeFn<string>;
}

export interface DataTableProps<TData> {
  /** Required accessible label for the grid. */
  ariaLabel: string;
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;

  /** Provide to switch to server-driven sort/filter/pagination. Omit for fully client-side. */
  server?: DataTableServer<TData>;

  enableSorting?: boolean;
  enableRowSelection?: boolean | ((row: TData) => boolean);
  onSelectionChange?: (rows: TData[]) => void;
  /**
   * Bump this (e.g. an incrementing counter) to clear the grid's internal row
   * selection — for surfaces that mirror selection into their own state and then
   * act on it (bulk action), so the checkboxes don't stay checked after the
   * action clears the mirror. Changing the value resets selection to none.
   */
  selectionResetKey?: string | number;
  /**
   * Client mode only: fires with the filtered + sorted rows (pre-pagination)
   * whenever sort/filter/data change. Lets a surface that keeps its own export
   * or summary outside the grid mirror the order the user is looking at.
   */
  onSortedRowsChange?: (rows: TData[]) => void;
  /** Expandable detail rows. Ignored when `enableVirtualization` is set (incompatible row heights). */
  enableExpanding?: boolean;
  renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
  /**
   * Detail-panel expansion only: whether a row can expand. Rows that cannot expand
   * show no chevron and never render their detail panel. Defaults to every row.
   */
  getRowCanExpand?: (row: TData) => boolean;
  /** Row ids (from `getRowId`) expanded on first render, e.g. a deep-linked record. */
  initialExpandedRowIds?: string[];
  /** Detail-panel expansion only: show the toolbar "Expand all / Collapse all" toggle (tree mode always shows it). */
  enableExpandAll?: boolean;
  /**
   * Hierarchical drill-down: return a row's children to render an indented tree
   * (e.g. listing → domain → scan). Mutually exclusive with `renderExpandedRow`;
   * ignored when `enableVirtualization` is set.
   */
  getSubRows?: (row: TData) => TData[] | undefined;
  onRowClick?: (row: TData) => void;
  /**
   * Accessible label for a clickable row (only used when `onRowClick` is set).
   * Clickable rows are keyboard-focusable and activate on Enter/Space; this
   * gives screen-reader users the action context, e.g. `Open audit log for X`.
   */
  rowClickLabel?: (row: TData) => string;
  /**
   * Per-row `data-testid` for targeting a specific row in tests (e.g.
   * `job-row-${row.key}`). Applied to the row element in both the standard and
   * virtualized render paths.
   */
  rowTestId?: (row: TData) => string;

  enablePagination?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  initialSorting?: SortingState;

  enableGlobalFilter?: boolean;
  searchPlaceholder?: string;

  enableCsvExport?: boolean;
  exportFileName?: string;
  /** Export-only columns appended to the CSV (e.g. the row UUID) — never displayed. */
  csvExtraColumns?: CsvExtraColumn<TData>[];
  /**
   * Server-paginated tables: resolve the FULL filtered row set for export (the table
   * only holds the current page). When provided, the Export button awaits this and
   * builds the CSV from all returned rows instead of just the visible page. Resolve
   * errors/truncation notices in the caller; the button shows a spinner while it runs.
   */
  exportAllRows?: () => Promise<TData[]>;

  enableVirtualization?: boolean;
  virtualRowHeight?: number;
  overscan?: number;
  maxBodyHeight?: number | string;

  /**
   * Drag-resizable columns (handle on each header's right edge; double-click
   * resets a column to its meta width). Pass `columnSizingStorageKey` to
   * remember widths across visits.
   */
  enableColumnResizing?: boolean;
  /**
   * localStorage key (namespaced by the table) for persisted column widths,
   * e.g. `'admin-marketplace'`. Only read/written when `enableColumnResizing`
   * is set; omit for session-only resizing.
   */
  columnSizingStorageKey?: string;
  /**
   * Drag-reorderable columns via a grip handle that appears on header hover.
   * Selection/expander columns stay pinned first. Pass `columnOrderStorageKey`
   * to remember the order across visits.
   */
  enableColumnReordering?: boolean;
  columnOrderStorageKey?: string;
  /**
   * Arrow-key row navigation (↑/↓, Home/End, PageUp/PageDown scroll + move
   * focus; Enter opens the row, Space toggles selection) plus a keyboard-help
   * popover in the toolbar. Off unless explicitly enabled (defaults to `false`).
   */
  enableKeyboardNavigation?: boolean;

  density?: DataTableDensity;
  stickyHeader?: boolean;

  loading?: boolean;
  loadingRows?: number;
  loadingMessage?: string;
  emptyState?: React.ReactNode;
  errorState?: React.ReactNode;

  itemLabel?: string;
  toolbarActions?: React.ReactNode;
  /**
   * Filter controls rendered on the toolbar's left side, next to the search box
   * (status chips, type selects, bulk-selection actions). `toolbarActions` stays
   * the right-side slot for page-level actions (export sits there too).
   */
  toolbarFilters?: React.ReactNode;
  className?: string;
  gridClassName?: string;
  bodyRowClassName?: string;
  selectedRowClassName?: string;
  /** Conditional per-row highlight derived from the row's data (e.g. flag critical / edited rows). */
  rowClassName?: (row: TData) => string | undefined;
  /**
   * Report a column's sort state for the header's `aria-sort` when sorting is driven
   * externally (server-side, `enableSorting={false}`) rather than by TanStack. Return
   * `'ascending'`/`'descending'` for the ACTIVE sorted column and `undefined` for
   * every other column (WAI-ARIA: only the sorted header owns `aria-sort`). When it
   * returns `undefined`, the header falls back to the client sort state.
   */
  getColumnAriaSort?: (columnId: string) => 'ascending' | 'descending' | 'none' | undefined;
  testId?: string;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_LOADING_ROWS = 5;
// A real search input for the "/" shortcut: the built-in filter and caller-supplied
// toolbar search inputs are both labelled "Search…". Filter dropdowns are not.
const SEARCH_INPUT_SELECTOR = 'input[type="search"], input[aria-label^="Search" i]';

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-none text-muted-foreground">
      {children}
    </kbd>
  );
}

export function DataTable<TData>({
  ariaLabel,
  columns,
  data,
  getRowId,
  server,
  enableSorting = true,
  enableRowSelection = false,
  onSelectionChange,
  selectionResetKey,
  onSortedRowsChange,
  enableExpanding = false,
  renderExpandedRow,
  getRowCanExpand,
  initialExpandedRowIds,
  enableExpandAll = false,
  getSubRows,
  onRowClick,
  rowClickLabel,
  rowTestId,
  enablePagination = false,
  initialPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = [10, 25, 50, 100],
  showPageSizeSelector = true,
  initialSorting = [],
  enableGlobalFilter = false,
  searchPlaceholder = 'Search…',
  enableCsvExport = false,
  exportFileName = 'export',
  csvExtraColumns,
  exportAllRows,
  enableVirtualization = false,
  virtualRowHeight = 56,
  overscan = 8,
  maxBodyHeight = 480,
  enableColumnResizing = false,
  columnSizingStorageKey,
  enableColumnReordering = false,
  columnOrderStorageKey,
  enableKeyboardNavigation,
  density = 'comfortable',
  stickyHeader = true,
  loading = false,
  loadingRows = DEFAULT_LOADING_ROWS,
  loadingMessage = 'Loading…',
  emptyState,
  errorState,
  itemLabel = 'rows',
  toolbarActions,
  toolbarFilters,
  className,
  gridClassName,
  bodyRowClassName,
  selectedRowClassName,
  rowClassName,
  getColumnAriaSort,
  testId,
}: DataTableProps<TData>) {
  const isServer = Boolean(server);

  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [expanded, setExpanded] = React.useState<ExpandedState>(() =>
    Object.fromEntries((initialExpandedRowIds ?? []).map((id) => [id, true])),
  );
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  // Seeded once from storage; the pointer-drag handler below mutates it and the
  // effect writes it back when the drag settles. Only touched when resizing is on.
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(() =>
    enableColumnResizing ? loadColumnSizing(columnSizingStorageKey) : {},
  );
  // Saved column order (may reference removed columns; sanitized before use).
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() =>
    enableColumnReordering ? loadColumnOrder(columnOrderStorageKey) : [],
  );
  const [draggingColumnId, setDraggingColumnId] = React.useState<string | null>(null);

  // Hierarchical drill-down (getSubRows) and detail-panel expansion (renderExpandedRow)
  // are mutually exclusive, and both need variable row heights, so both are disabled
  // under virtualization (fixed-size rows).
  const treeMode = Boolean(getSubRows) && !enableVirtualization;
  const canExpand = enableExpanding && Boolean(renderExpandedRow) && !enableVirtualization && !treeMode;
  const canSelectRow = React.useCallback(
    (row: Row<TData>) => (typeof enableRowSelection === 'function' ? enableRowSelection(row.original) : enableRowSelection),
    [enableRowSelection],
  );

  const baseColumns = React.useMemo<ColumnDef<TData, unknown>[]>(() => {
    const next: ColumnDef<TData, unknown>[] = [];

    if (treeMode) {
      next.push({
        id: '__tree__',
        header: '',
        enableSorting: false,
        enableResizing: false,
        meta: { width: 'auto', exportable: false },
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              style={{ marginLeft: `${row.depth * 1.25}rem` }}
              onClick={(event) => {
                event.stopPropagation();
                row.toggleExpanded();
              }}
              aria-label={`${row.getIsExpanded() ? 'Collapse' : 'Expand'} ${getRowAccessibleLabel(
                row.original as Record<string, unknown>,
                row.id,
              )}`}
            >
              {row.getIsExpanded() ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          ) : (
            <span className="inline-block h-7 w-7" style={{ marginLeft: `${row.depth * 1.25}rem` }} aria-hidden />
          ),
      });
    } else if (canExpand) {
      next.push({
        id: '__expander__',
        header: '',
        enableSorting: false,
        enableResizing: false,
        meta: { width: '52px', align: 'center', exportable: false },
        cell: ({ row }) => (
          <div className="flex justify-center">
            {row.getCanExpand() ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(event) => {
                event.stopPropagation();
                row.toggleExpanded();
              }}
              aria-label={`${row.getIsExpanded() ? 'Collapse' : 'Expand'} row ${getRowAccessibleLabel(
                row.original as Record<string, unknown>,
                row.id,
              )}`}
              aria-expanded={row.getIsExpanded()}
            >
              {row.getIsExpanded() ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            ) : null}
          </div>
        ),
      });
    }

    if (enableRowSelection) {
      next.push({
        id: '__select__',
        enableSorting: false,
        enableResizing: false,
        meta: { width: '52px', align: 'center', sticky: 'left', exportable: false },
        header: ({ table }) => (
          <div className="flex justify-center">
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
              }
              onCheckedChange={(checked) => table.toggleAllPageRowsSelected(!!checked)}
              aria-label="Select all rows"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(checked) => row.toggleSelected(!!checked)}
              onClick={(event) => event.stopPropagation()}
              disabled={!row.getCanSelect()}
              aria-label={`Select row ${getRowAccessibleLabel(row.original as Record<string, unknown>, row.id)}`}
            />
          </div>
        ),
      });
    }

    next.push(...columns);
    return next;
  }, [canExpand, columns, enableRowSelection, treeMode]);

  // Sticky columns are excluded from reordering entirely (no grip, not a drop
  // target, immune in saved orders): letting a frozen column move into the
  // scrolling region — or vice versa — would leave the cell pinned over
  // unrelated columns.
  const leafColumnMeta = React.useMemo(() => {
    const allLeafIds: string[] = [];
    const pinnedIds: string[] = [];
    for (const column of baseColumns) {
      const id = column.id ?? ((column as { accessorKey?: unknown }).accessorKey as string | undefined);
      if (typeof id !== 'string') {
        continue;
      }
      allLeafIds.push(id);
      if (isSyntheticColumn(id) || getColumnMeta(column)?.sticky !== undefined) {
        pinnedIds.push(id);
      }
    }
    return { allLeafIds, pinnedIds, pinnedSet: new Set(pinnedIds) };
  }, [baseColumns]);

  // A real frozen column = a pinned column that isn't the synthetic
  // select/expander/tree column. Only these tables need the solid opaque row
  // backgrounds; everything else keeps the original translucent look.
  const hasFrozenColumns = leafColumnMeta.pinnedIds.some((id) => !isSyntheticColumn(id));

  // Saved order reconciled against the actual columns (handles added/removed
  // columns since the order was saved; synthetic + sticky columns keep their
  // default positions).
  const sanitizedColumnOrder = React.useMemo(() => {
    if (!enableColumnReordering || columnOrder.length === 0) {
      return undefined;
    }
    return sanitizeColumnOrder(columnOrder, leafColumnMeta.allLeafIds, leafColumnMeta.pinnedIds);
  }, [enableColumnReordering, columnOrder, leafColumnMeta]);

  const table = useReactTable({
    data,
    columns: baseColumns,
    getRowId,
    getSubRows: treeMode ? getSubRows : undefined,
    state: {
      sorting: isServer ? server!.sorting : sorting,
      rowSelection,
      expanded,
      globalFilter: isServer ? server!.globalFilter ?? '' : globalFilter,
      ...(enablePagination ? { pagination: isServer ? server!.pagination : pagination } : {}),
      ...(sanitizedColumnOrder ? { columnOrder: sanitizedColumnOrder } : {}),
    },
    onColumnOrderChange: setColumnOrder,
    enableSorting,
    enableRowSelection: canSelectRow,
    enableExpanding: canExpand || treeMode,
    // Detail-panel mode: every row can expand unless the caller narrows it (TanStack's
    // own default keys on sub-rows, which detail panels never have).
    getRowCanExpand: canExpand ? (row) => (getRowCanExpand ? getRowCanExpand(row.original) : true) : undefined,
    // Only consulted via column.getCanResize() — sizing itself is driven by the
    // pointer handler below (TanStack's own drag starts from its synthetic 150px
    // default, which jumps for fr-width columns; we measure the rendered width).
    enableColumnResizing,
    manualSorting: isServer,
    manualFiltering: isServer,
    manualPagination: isServer,
    rowCount: isServer ? server!.rowCount : undefined,
    globalFilterFn: 'includesString',
    onSortingChange: isServer ? server!.onSortingChange : setSorting,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onGlobalFilterChange: isServer ? server!.onGlobalFilterChange : setGlobalFilter,
    onPaginationChange: isServer ? server!.onPaginationChange : setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isServer ? undefined : getSortedRowModel(),
    getFilteredRowModel: isServer ? undefined : getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: enablePagination && !isServer ? getPaginationRowModel() : undefined,
  });

  React.useEffect(() => {
    if (!onSelectionChange) {
      return;
    }
    onSelectionChange(table.getSelectedRowModel().rows.map((row) => row.original));
  }, [onSelectionChange, rowSelection, table]);

  React.useEffect(() => {
    if (!onSortedRowsChange) {
      return;
    }
    onSortedRowsChange(table.getSortedRowModel().rows.map((row) => row.original));
  }, [onSortedRowsChange, sorting, globalFilter, data, table]);

  // Clear internal selection when the surface bumps selectionResetKey (e.g. after
  // a bulk action consumes the mirrored selection). A no-op on mount (already {}).
  React.useEffect(() => {
    setRowSelection({});
  }, [selectionResetKey]);

  const visibleColumns = table.getVisibleLeafColumns();
  // Resized columns get their dragged pixel width; untouched columns keep their
  // meta width (fr columns stay flexible) so one resize doesn't freeze the table.
  const gridTemplateColumns = React.useMemo(
    () =>
      visibleColumns
        .map((column) => (columnSizing[column.id] !== undefined ? `${columnSizing[column.id]}px` : getColumnWidth(column)))
        .join(' '),
    [visibleColumns, columnSizing],
  );
  // Sum of column minimums — the table never shrinks below this; narrower
  // viewports (mobile) scroll horizontally instead of clipping columns.
  const minTableWidth = React.useMemo(
    () =>
      visibleColumns.reduce(
        (total, column) => total + (columnSizing[column.id] ?? minColumnWidth(getColumnWidth(column))),
        0,
      ),
    [visibleColumns, columnSizing],
  );

  const MIN_RESIZE_WIDTH = 64;
  const [resizingColumnId, setResizingColumnId] = React.useState<string | null>(null);

  // Pointer-driven column resize: measure the header cell's real rendered width
  // at drag start (correct for fr/minmax columns), then track the pointer delta.
  const startColumnResize = React.useCallback((event: React.PointerEvent<HTMLElement>, columnId: string) => {
    const headerCell = event.currentTarget.parentElement;
    if (!headerCell) {
      return;
    }
    event.preventDefault();
    const startWidth = headerCell.getBoundingClientRect().width;
    const startX = event.clientX;
    setResizingColumnId(columnId);
    const handleMove = (moveEvent: PointerEvent) => {
      const next = Math.max(MIN_RESIZE_WIDTH, Math.round(startWidth + (moveEvent.clientX - startX)));
      setColumnSizing((previous) => (previous[columnId] === next ? previous : { ...previous, [columnId]: next }));
    };
    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      setResizingColumnId(null);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }, []);

  const resetColumnSize = React.useCallback((columnId: string) => {
    setColumnSizing((previous) => {
      if (previous[columnId] === undefined) {
        return previous;
      }
      const { [columnId]: _removed, ...rest } = previous;
      return rest;
    });
  }, []);

  // Remember widths once the drag settles (not per pointermove frame).
  React.useEffect(() => {
    if (!enableColumnResizing || resizingColumnId !== null) {
      return;
    }
    persistColumnSizing(columnSizingStorageKey, columnSizing);
  }, [enableColumnResizing, resizingColumnId, columnSizingStorageKey, columnSizing]);

  // Remember column order (only changes on drop).
  React.useEffect(() => {
    if (!enableColumnReordering || columnOrder.length === 0) {
      return;
    }
    persistColumnOrder(columnOrderStorageKey, columnOrder);
  }, [enableColumnReordering, columnOrderStorageKey, columnOrder]);

  /** Move `sourceId` to `targetId`'s position in the visible column order (keyboard reorder). */
  const moveColumn = React.useCallback(
    (sourceId: string, targetId: string) => {
      if (sourceId === targetId) {
        return;
      }
      const ids = table.getVisibleLeafColumns().map((column) => column.id);
      const from = ids.indexOf(sourceId);
      const to = ids.indexOf(targetId);
      if (from === -1 || to === -1) {
        return;
      }
      ids.splice(from, 1);
      ids.splice(to, 0, sourceId);
      setColumnOrder(ids);
    },
    [table],
  );

  /** Keyboard reorder: move a column one visible position left/right, skipping synthetic columns. */
  const moveColumnByOffset = React.useCallback(
    (columnId: string, delta: -1 | 1) => {
      const ids = table.getVisibleLeafColumns().map((column) => column.id);
      const from = ids.indexOf(columnId);
      let to = from + delta;
      while (to >= 0 && to < ids.length && leafColumnMeta.pinnedSet.has(ids[to])) {
        to += delta;
      }
      if (from === -1 || to < 0 || to >= ids.length) {
        return;
      }
      moveColumn(columnId, ids[to]);
      // Keep focus on the grip after React re-renders the reordered header.
      requestAnimationFrame(() => {
        tableRef.current
          ?.querySelector<HTMLElement>(`[data-testid$="reorder-${columnId}"], [data-dt-reorder="${columnId}"]`)
          ?.focus();
      });
    },
    [table, moveColumn, leafColumnMeta],
  );

  // Pointer-driven column reorder using the standard data-grid pattern
  // (AG Grid / Airtable): a floating clone of the header follows the pointer,
  // and the columns reorder LIVE as the pointer crosses their midpoints — the
  // other columns visibly move aside to make space. Releasing simply keeps the
  // current order (no drop step, nothing to snap back).
  const startColumnReorder = React.useCallback(
    (event: React.PointerEvent<HTMLElement>, columnId: string) => {
      event.preventDefault();
      setDraggingColumnId(columnId);

      // Floating header ghost.
      const headerCell = event.currentTarget.closest<HTMLElement>('[data-dt-column-id]');
      const rect = headerCell?.getBoundingClientRect();
      let ghost: HTMLElement | null = null;
      if (headerCell && rect) {
        ghost = headerCell.cloneNode(true) as HTMLElement;
        ghost.style.cssText = [
          'position: fixed',
          `top: ${rect.top}px`,
          `left: ${rect.left}px`,
          `width: ${rect.width}px`,
          `height: ${rect.height}px`,
          'z-index: 9999',
          'pointer-events: none',
          'opacity: 0.9',
          'background: hsl(var(--background))',
          'border: 1px solid hsl(var(--border))',
          'border-radius: 6px',
          'box-shadow: 0 8px 24px -6px rgb(0 0 0 / 0.25)',
        ].join('; ');
        document.body.appendChild(ghost);
      }
      const grabOffsetX = rect ? event.clientX - rect.left : 0;
      const grabOffsetY = rect ? event.clientY - rect.top : 0;

      const handleMove = (moveEvent: PointerEvent) => {
        if (ghost && rect) {
          ghost.style.transform = `translate(${moveEvent.clientX - rect.left - grabOffsetX}px, ${
            moveEvent.clientY - rect.top - grabOffsetY
          }px)`;
        }
        // Deterministic insertion slot from header midpoints (jitter-free: the
        // pointer inside the dragged column's own cell resolves to "no move").
        const cells = Array.from(
          tableRef.current?.querySelectorAll<HTMLElement>('[role="columnheader"][data-dt-column-id]') ?? [],
        ).filter((cell) => !leafColumnMeta.pinnedSet.has(cell.dataset.dtColumnId ?? ''));
        if (cells.length === 0) {
          return;
        }
        const midpoints = cells.map((cell) => {
          const cellRect = cell.getBoundingClientRect();
          return cellRect.left + cellRect.width / 2;
        });
        const insertAt = midpoints.filter((midpoint) => moveEvent.clientX > midpoint).length;

        const allIds = table.getVisibleLeafColumns().map((column) => column.id);
        const flowing = allIds.filter((id) => !leafColumnMeta.pinnedSet.has(id));
        const from = flowing.indexOf(columnId);
        let to = insertAt > from ? insertAt - 1 : insertAt;
        to = Math.max(0, Math.min(flowing.length - 1, to));
        if (from === -1 || to === from) {
          return;
        }
        const nextFlowing = [...flowing];
        nextFlowing.splice(from, 1);
        nextFlowing.splice(to, 0, columnId);
        let cursor = 0;
        setColumnOrder(allIds.map((id) => (leafColumnMeta.pinnedSet.has(id) ? id : nextFlowing[cursor++])));
      };

      const handleUp = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        ghost?.remove();
        setDraggingColumnId(null);
      };
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [table, leafColumnMeta],
  );

  // Cumulative left offsets so multiple sticky-left columns stack instead of
  // all pinning at left: 0. Sticky columns should use px widths; fr columns
  // fall back to their minimum width for offset purposes.
  const stickyLeftOffsets = React.useMemo(() => {
    const offsets: Record<string, number> = {};
    let acc = 0;
    for (const column of visibleColumns) {
      if (getColumnMeta(column.columnDef)?.sticky === 'left') {
        offsets[column.id] = acc;
        acc += columnSizing[column.id] ?? minColumnWidth(getColumnWidth(column));
      }
    }
    return offsets;
  }, [visibleColumns, columnSizing]);

  const stickyStyle = (columnId: string, sticky?: string): React.CSSProperties | undefined =>
    sticky === 'left' ? { left: stickyLeftOffsets[columnId] } : undefined;

  const rows = table.getRowModel().rows;
  const totalColumns = visibleColumns.length;
  const totalItems = isServer ? server!.rowCount : table.getFilteredRowModel().rows.length;

  const tableRef = React.useRef<HTMLDivElement>(null);
  const scrollParentRef = React.useRef<HTMLDivElement>(null);
  // Wraps the toolbar + grid, so the `/` shortcut can reach the search input
  // (which may be the built-in filter OR a caller-supplied one in toolbarFilters).
  const rootRef = React.useRef<HTMLDivElement>(null);
  // Whether the shortcuts popover advertises "/" — recomputed live each time it
  // opens (see the Popover's onOpenChange), so it stays correct even if a toolbar
  // search input appears/disappears after mount.
  const [searchShortcutAvailable, setSearchShortcutAvailable] = React.useState(false);
  const virtualizer = useVirtualizer({
    count: enableVirtualization ? rows.length : 0,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => virtualRowHeight,
    overscan,
  });
  const virtualRows = enableVirtualization ? virtualizer.getVirtualItems() : [];

  // Keyboard row navigation is OPT-IN (default off) so existing tables aren't
  // silently given a shortcuts popover + arrow-key handling; surfaces that want
  // it pass `enableKeyboardNavigation`.
  const keyboardNav = enableKeyboardNavigation ?? false;
  const [keyboardActiveIndex, setKeyboardActiveIndex] = React.useState<number | null>(null);

  const focusRow = React.useCallback(
    (index: number) => {
      setKeyboardActiveIndex(index);
      const focusRowElement = () => {
        const element = tableRef.current?.querySelector<HTMLElement>(`[data-row-index="${index}"]`);
        if (element) {
          element.focus({ preventScroll: true });
          element.scrollIntoView?.({ block: 'nearest' });
        }
      };
      if (enableVirtualization) {
        virtualizer.scrollToIndex(index);
        // Focus synchronously when the row is already mounted; only defer (for
        // the virtualizer to render it) when it isn't.
        if (tableRef.current?.querySelector(`[data-row-index="${index}"]`)) {
          focusRowElement();
        } else {
          requestAnimationFrame(() => requestAnimationFrame(focusRowElement));
        }
      } else {
        focusRowElement();
      }
    },
    [enableVirtualization, virtualizer],
  );

  const handleTableKeyDown = (event: React.KeyboardEvent) => {
    if (!keyboardNav || rows.length === 0) {
      return;
    }
    const max = rows.length - 1;
    const current = keyboardActiveIndex ?? -1;
    let next: number;
    switch (event.key) {
      case 'ArrowDown':
        next = Math.min(max, current + 1);
        break;
      case 'ArrowUp':
        next = Math.max(0, current - 1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = max;
        break;
      case 'PageDown':
        next = Math.min(max, current + 10);
        break;
      case 'PageUp':
        next = Math.max(0, current - 10);
        break;
      default:
        return;
    }
    event.preventDefault();
    focusRow(next);
  };

  const headerHeightClassName = density === 'compact' ? 'min-h-10' : 'min-h-12';
  const rowHeightClassName = density === 'compact' ? 'min-h-12' : 'min-h-14';
  const cellPaddingClassName = density === 'compact' ? 'px-3 py-2' : 'px-4 py-3';

  const [isExporting, setIsExporting] = React.useState(false);
  const handleExport = React.useCallback(async () => {
    if (!exportAllRows) {
      downloadCsv(exportFileName, buildCsv(table, csvExtraColumns));
      return;
    }
    // Server-paginated: fetch the whole filtered set, then build the CSV from it.
    setIsExporting(true);
    try {
      const rows = await exportAllRows();
      downloadCsv(exportFileName, buildCsv(table, csvExtraColumns, rows));
    } catch (error) {
      // The resolver is responsible for user-facing error messaging.
      console.error('CSV export failed', error);
    } finally {
      setIsExporting(false);
    }
  }, [exportAllRows, exportFileName, table, csvExtraColumns]);

  const supportsLayoutChanges = enableColumnResizing || enableColumnReordering;
  const hasLayoutChanges = Object.keys(columnSizing).length > 0 || columnOrder.length > 0;

  /** Reset resized widths + reordered columns back to the column-meta defaults. */
  const resetTableLayout = React.useCallback(() => {
    setColumnSizing({});
    setColumnOrder([]);
    // The persist effects skip empty state, so clear storage explicitly.
    persistColumnSizing(columnSizingStorageKey, {});
    persistColumnOrder(columnOrderStorageKey, []);
  }, [columnSizingStorageKey, columnOrderStorageKey]);

  const showExpandAll = treeMode || (canExpand && enableExpandAll);
  // Detail-panel mode counts only rows that can expand, so a table with some
  // non-expandable rows still reads "Collapse all" once every expandable row is open.
  const expandableRows = canExpand ? table.getCoreRowModel().rows.filter((row) => row.getCanExpand()) : [];
  const allRowsExpanded = canExpand
    ? expandableRows.length > 0 && expandableRows.every((row) => row.getIsExpanded())
    : table.getIsAllRowsExpanded();
  const toggleAllExpanded = () => {
    if (!canExpand) {
      table.toggleAllRowsExpanded();
      return;
    }
    setExpanded(allRowsExpanded ? {} : Object.fromEntries(expandableRows.map((row) => [row.id, true])));
  };
  const showToolbar =
    enableGlobalFilter ||
    enableCsvExport ||
    Boolean(toolbarActions) ||
    Boolean(toolbarFilters) ||
    treeMode ||
    showExpandAll ||
    supportsLayoutChanges ||
    // Keyboard nav puts its shortcuts-help popover in the toolbar, so the
    // toolbar must render even when it's the only enabled toolbar feature.
    keyboardNav;
  const showPager = enablePagination && totalItems > 0;
  const withTestId = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);

  // "/" focuses the table's search box — the web convention (GitHub, YouTube, …).
  // The listener is registered whenever the table *may* have search (built-in filter
  // or any toolbarFilters), but the handler resolves the search box LIVE at keypress
  // by querying only `Search…`-labelled inputs. So it's always current if the toolbar
  // changes, and it never hijacks "/" on a filter-only table (no search input → the
  // key stays a literal character). Also skips while typing or when a modifier held.
  const mightHaveSearch = enableGlobalFilter || Boolean(toolbarFilters);
  const resolveSearchInput = React.useCallback(
    () => rootRef.current?.querySelector<HTMLInputElement>(SEARCH_INPUT_SELECTOR) ?? null,
    [],
  );
  React.useEffect(() => {
    if (!mightHaveSearch) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) {
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.tagName === 'SELECT' ||
          active.isContentEditable)
      ) {
        return;
      }
      const searchInput = resolveSearchInput();
      if (!searchInput) return;
      event.preventDefault();
      searchInput.focus();
      searchInput.select?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mightHaveSearch, resolveSearchInput]);

  function renderCells(row: Row<TData>) {
    return row.getVisibleCells().map((cell) => {
      const meta = getColumnMeta(cell.column.columnDef);
      return (
        <div
          key={cell.id}
          role="cell"
          style={stickyStyle(cell.column.id, meta?.sticky)}
          className={cn(
            // A grid item's automatic minimum is its content width. Allow the
            // canonical column track to win so narrow advisory tables wrap their
            // contents rather than widening the whole table and hiding actions.
            'flex min-w-0 items-center border-r border-border/50 text-sm last:border-r-0',
            rowHeightClassName,
            cellPaddingClassName,
            getAlignmentClassName(meta?.align),
            getStickyClassName(meta?.sticky),
            // Frozen cells carry the row's own background (opaque) so they hide
            // the columns scrolling beneath them AND still show the row's
            // hover/selected/active highlight. `group-hover` mirrors the row's
            // hover because the hover target is the row, not the cell.
            meta?.sticky && rowBgClasses(row, 'group-hover'),
            meta?.cellClassName,
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      );
    });
  }

  // Single source of truth for a row's background in each state, reused by the
  // row (for its transparent non-frozen cells) and by the frozen cells (so they
  // stay opaque and match). `hoverScope` selects the literal hover variant —
  // 'hover' for the row itself, 'group-hover' for a frozen cell reacting to the
  // row's hover. Literal class names keep Tailwind's JIT happy.
  //
  // Tables WITHOUT frozen columns keep the original translucent hover/selected
  // look (no visual change for existing consumers). Frozen tables need SOLID
  // backgrounds so the sticky cells — which reuse these classes — stay opaque
  // over horizontally-scrolled columns while still showing the highlight.
  function rowBgClasses(row: Row<TData>, hoverScope: 'hover' | 'group-hover') {
    const custom = rowClassName?.(row.original);
    if (custom) {
      return custom; // caller owns the highlight (incl. its own hover behaviour)
    }
    const isSelected = row.getIsSelected();
    if (!hasFrozenColumns) {
      if (isSelected) {
        return selectedRowClassName ?? 'bg-muted/60';
      }
      return onRowClick ? 'hover:bg-muted/50' : '';
    }
    if (isSelected) {
      return (
        selectedRowClassName ??
        (hoverScope === 'hover' ? 'bg-accent hover:bg-accent/70' : 'bg-accent group-hover:bg-accent/70')
      );
    }
    return cn('bg-background', onRowClick && (hoverScope === 'hover' ? 'hover:bg-muted' : 'group-hover:bg-muted'));
  }

  function bodyRowClasses(row: Row<TData>) {
    return cn(
      // `group` so frozen cells can mirror the row's hover via group-hover.
      'group grid items-center border-t transition-colors',
      rowHeightClassName,
      onRowClick && 'cursor-pointer',
      (onRowClick || keyboardNav) && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
      rowBgClasses(row, 'hover'),
      !row.getIsSelected() && bodyRowClassName,
    );
  }

  // Interactive rows are keyboard-accessible: focusable, Enter opens the row,
  // Space toggles selection (when enabled) or opens the row otherwise, with an
  // optional accessible label. Returns no handlers when the grid is inert.
  function rowInteractionProps(row: Row<TData>, index: number) {
    if (!onRowClick && !keyboardNav) {
      return {};
    }
    return {
      tabIndex: 0,
      'data-row-index': index,
      'aria-label': rowClickLabel?.(row.original),
      onFocus: () => setKeyboardActiveIndex(index),
      onClick: onRowClick ? () => onRowClick(row.original) : undefined,
      onKeyDown: (event: React.KeyboardEvent) => {
        // Only activate when the row itself is focused — not when Enter/Space
        // bubbles up from an interactive child (e.g. the row-actions menu button
        // or a cell button), which would wrongly trigger row navigation.
        if (event.target !== event.currentTarget) {
          return;
        }
        if (event.key === ' ' && enableRowSelection && row.getCanSelect()) {
          event.preventDefault();
          row.toggleSelected();
          return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && onRowClick) {
          event.preventDefault();
          onRowClick(row.original);
        }
      },
    };
  }

  return (
    <div ref={rootRef} className={cn('space-y-3', className)} data-testid={testId}>
      {showToolbar ? (
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid={withTestId('toolbar')}>
          <div className="flex flex-1 items-center gap-2">
            {showExpandAll ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={toggleAllExpanded}
                aria-label={allRowsExpanded ? 'Collapse all rows' : 'Expand all rows'}
              >
                {allRowsExpanded ? (
                  <FoldVertical className="h-4 w-4 sm:mr-2" />
                ) : (
                  <UnfoldVertical className="h-4 w-4 sm:mr-2" />
                )}
                <span className="hidden sm:inline">
                  {allRowsExpanded ? 'Collapse all' : 'Expand all'}
                </span>
              </Button>
            ) : null}
            {enableGlobalFilter ? (
              <div className="relative w-full max-w-xs">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={table.getState().globalFilter as string}
                  onChange={(event) => table.setGlobalFilter(event.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={`Search ${itemLabel}`}
                  className="pl-8"
                />
              </div>
            ) : null}
            {toolbarFilters}
          </div>
          <div className="flex items-center gap-2">
            {toolbarActions}
            {keyboardNav ? (
              <Popover
                onOpenChange={(open) => {
                  // Re-detect a real search box each time the help opens, so the
                  // "/" row reflects the current toolbar (search may be conditional).
                  if (open) setSearchShortcutAvailable(Boolean(resolveSearchInput()));
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Keyboard shortcuts"
                    title="Keyboard shortcuts"
                    data-testid={withTestId('keyboard-help')}
                  >
                    <Keyboard className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72">
                  <p className="mb-2 text-sm font-medium">Keyboard shortcuts</p>
                  <dl className="space-y-1.5 text-sm">
                    {[
                      ...(searchShortcutAvailable ? [{ keys: ['/'], action: 'Search' }] : []),
                      { keys: ['↑', '↓'], action: 'Move between rows' },
                      { keys: ['Home', 'End'], action: 'First / last row' },
                      { keys: ['PgUp', 'PgDn'], action: 'Jump 10 rows' },
                      ...(onRowClick ? [{ keys: ['Enter'], action: 'Open row' }] : []),
                      ...(enableRowSelection ? [{ keys: ['Space'], action: 'Select row' }] : []),
                      ...(enableColumnReordering
                        ? [{ keys: ['⋮⋮', '←', '→'], action: 'Reorder column (drag or arrows on grip)' }]
                        : []),
                      ...(enableColumnResizing
                        ? [
                            { keys: ['Drag edge'], action: 'Resize column' },
                            { keys: ['2× edge'], action: 'Reset column width' },
                          ]
                        : []),
                    ].map((shortcut) => (
                      <div key={shortcut.action} className="flex items-center justify-between gap-3">
                        <dt className="flex items-center gap-1">
                          {shortcut.keys.map((key) => (
                            <Kbd key={key}>{key}</Kbd>
                          ))}
                        </dt>
                        <dd className="text-muted-foreground">{shortcut.action}</dd>
                      </div>
                    ))}
                  </dl>
                </PopoverContent>
              </Popover>
            ) : null}
            {supportsLayoutChanges ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetTableLayout}
                disabled={!hasLayoutChanges}
                aria-label="Reset table layout"
                title="Reset column widths and order"
                data-testid={withTestId('reset-layout')}
              >
                <RotateCcw className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Reset layout</span>
              </Button>
            ) : null}
            {enableCsvExport ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={isExporting}
                aria-label="Export CSV"
                aria-busy={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
                ) : (
                  <Download className="h-4 w-4 sm:mr-2" />
                )}
                <span className="hidden sm:inline">{isExporting ? 'Exporting…' : 'Export'}</span>
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        ref={tableRef}
        role="table"
        aria-label={ariaLabel}
        aria-rowcount={isServer ? server!.rowCount : rows.length}
        onKeyDown={handleTableKeyDown}
        className={cn(
          'overflow-hidden rounded-md border bg-background',
          draggingColumnId && 'cursor-grabbing select-none',
          gridClassName,
        )}
      >
        <div className="overflow-x-auto">
          <div style={{ minWidth: `${minTableWidth}px` }}>
          <div
            role="rowgroup"
            // Frozen tables need a solid header (so frozen header cells sharing
            // this shade stay opaque over scrolled columns); non-frozen tables
            // keep the original translucent header — no change for existing
            // consumers.
            className={cn(
              'border-b',
              hasFrozenColumns ? 'bg-muted' : 'bg-muted/40 backdrop-blur-sm',
              stickyHeader && 'sticky top-0 z-30',
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <div
                key={headerGroup.id}
                role="row"
                className={cn('grid items-center', headerHeightClassName)}
                style={{ gridTemplateColumns }}
              >
                {headerGroup.headers.map((header) => {
                  const meta = getColumnMeta(header.column.columnDef);
                  const isSortable = header.column.getCanSort();
                  const sortState = header.column.getIsSorted();
                  const isReorderable = enableColumnReordering && !leafColumnMeta.pinnedSet.has(header.column.id);
                  // Accessible name/description for the header control (ENT-4272):
                  // a render-function header otherwise falls back to the column id
                  // for its aria-label, and a hover-only tooltip definition is not
                  // announced. `headerSortLabel` fixes the sort name; a
                  // `headerDescription` is exposed via aria-describedby + title so a
                  // keyboard/screen-reader user hears it on focus.
                  const headerSortName = meta?.headerSortLabel
                    ?? headerToText(header.column.columnDef.header, header.column.id);
                  const headerDescId = meta?.headerDescription
                    ? `dt-hdr-desc-${header.column.id}`
                    : undefined;
                  return (
                    <div
                      key={header.id}
                      role="columnheader"
                      aria-sort={
                        getColumnAriaSort?.(header.column.id) ??
                        (sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : undefined)
                      }
                      data-dt-column-id={enableColumnReordering ? header.column.id : undefined}
                      style={{ ...stickyStyle(header.column.id, meta?.sticky), containerType: 'inline-size' }}
                      className={cn(
                        // Reserve space for controls; the label may grow the header
                        // vertically rather than lose words when the column narrows.
                        'group/header flex items-center overflow-hidden border-r border-border/60 text-sm font-medium text-muted-foreground last:border-r-0',
                        (enableColumnResizing || enableColumnReordering) && 'relative',
                        // Dim the source column at its live position while its
                        // floating ghost follows the pointer (columns reorder live).
                        draggingColumnId === header.column.id && 'opacity-40',
                        headerHeightClassName,
                        cellPaddingClassName,
                        'px-2',
                        getAlignmentClassName(meta?.align),
                        getStickyClassName(meta?.sticky),
                        // Same solid shade as the header row — uniform (not darker)
                        // and opaque so scrolled header cells don't ghost through.
                        meta?.sticky && 'bg-muted',
                        meta?.headerClassName,
                      )}
                    >
                      {isReorderable ? (
                        <span
                          onPointerDown={(event) => startColumnReorder(event, header.column.id)}
                          onKeyDown={(event) => {
                            // Keyboard-operable reordering: ←/→ move the column
                            // one visible position (announced via aria-label).
                            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                              event.preventDefault();
                              event.stopPropagation();
                              moveColumnByOffset(header.column.id, event.key === 'ArrowLeft' ? -1 : 1);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Reorder ${headerToText(header.column.columnDef.header, header.column.id)} column — drag, or press the left and right arrow keys`}
                          title="Drag or press ←/→ to reorder column"
                          // Absolutely positioned in the left gutter so it reserves
                          // no inline width — the title uses the full column and
                          // does not wrap at its default size; the grip only appears
                          // on hover/focus.
                          className="absolute left-0 top-1/2 z-10 flex h-5 w-4 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded-sm bg-background/70 opacity-0 transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/header:opacity-100 active:cursor-grabbing"
                          data-dt-reorder={header.column.id}
                          data-testid={withTestId(`reorder-${header.column.id}`)}
                        >
                          <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                      ) : null}
                      {/* Always-on accessible description for assistive tech
                          (the sort button's aria-describedby target). */}
                      {meta?.headerDescription ? (
                        <span id={headerDescId} className="sr-only">{meta.headerDescription}</span>
                      ) : null}
                      {isSortable ? (() => {
                        const sortButton = (
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-auto w-full min-w-0 max-w-full whitespace-normal px-0 py-0 text-left font-medium text-muted-foreground hover:bg-transparent hover:text-foreground [&_svg]:size-[16px]"
                            onClick={header.column.getToggleSortingHandler()}
                            aria-label={`Sort by ${headerSortName}`}
                            aria-describedby={headerDescId}
                          >
                            <span data-dt-header-layout="label-help-sort" className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1">
                              <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere] text-left leading-tight [font-size:clamp(0.75rem,12cqi,0.875rem)]">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              {/* The definition control has its own fixed grid slot: a wrapped
                                  label never pushes it below, or underneath, the sort control. */}
                              {meta?.headerDescription ? <Info className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden /> : <span aria-hidden />}
                              {sortState === 'asc' ? (
                                <ChevronUp className="h-4 w-4 shrink-0" />
                              ) : sortState === 'desc' ? (
                                <ChevronDown className="h-4 w-4 shrink-0" />
                              ) : (
                                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                              )}
                            </span>
                          </Button>
                        );
                        // A header definition becomes a focus/hover tooltip anchored
                        // to the sort control itself — visible on keyboard focus (not
                        // hover-only) and portaled so the header's overflow-hidden
                        // does not clip it. A local provider keeps it self-contained
                        // for tables rendered outside the app's root provider.
                        return meta?.headerDescription ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>{sortButton}</TooltipTrigger>
                              <TooltipContent className="max-w-xs font-normal">
                                {meta.headerDescription}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          sortButton
                        );
                      })() : meta?.headerDescription ? (
                        // Unsortable header with a definition: the label itself is the
                        // focusable tooltip trigger, with a visible (i) affordance.
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                tabIndex={0}
                                aria-describedby={headerDescId}
                                data-dt-header-layout="label-help"
                                className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1 cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere] leading-tight [font-size:clamp(0.75rem,12cqi,0.875rem)]">
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                </span>
                                <Info className="h-[14px] w-[14px] shrink-0 opacity-70" aria-hidden />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs font-normal">{meta.headerDescription}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere] leading-tight [font-size:clamp(0.75rem,12cqi,0.875rem)]">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                      )}
                      {enableColumnResizing && header.column.getCanResize() ? (
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${headerToText(header.column.columnDef.header, header.column.id)} column`}
                          onPointerDown={(event) => startColumnResize(event, header.column.id)}
                          onDoubleClick={() => resetColumnSize(header.column.id)}
                          onClick={(event) => event.stopPropagation()}
                          className={cn(
                            'absolute right-0 top-0 z-10 h-full w-2 cursor-col-resize touch-none select-none',
                            'after:absolute after:inset-y-2 after:right-0 after:w-px after:bg-transparent after:transition-colors',
                            'hover:after:inset-y-0 hover:after:w-0.5 hover:after:bg-primary/50',
                            resizingColumnId === header.column.id && 'after:inset-y-0 after:w-0.5 after:bg-primary',
                          )}
                          title="Drag to resize · double-click to reset"
                          data-testid={withTestId(`resize-${header.column.id}`)}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

        {errorState ? (
          <div className="px-6 py-12 text-center text-sm text-destructive" role="alert">
            {errorState}
          </div>
        ) : loading ? (
          <div className="border-t" data-testid={withTestId('loading')}>
            <div className="px-4 py-3 text-sm text-muted-foreground">{loadingMessage}</div>
            <div role="rowgroup">
              {Array.from({ length: loadingRows }).map((_, index) => (
                <div
                  key={`loading-row-${index}`}
                  role="row"
                  data-testid="data-table-loading-row"
                  className={cn('grid items-center border-t', rowHeightClassName)}
                  style={{ gridTemplateColumns }}
                >
                  {Array.from({ length: totalColumns }).map((__, columnIndex) => (
                    <div key={`loading-cell-${index}-${columnIndex}`} role="cell" className={cellPaddingClassName}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid={withTestId('empty')}>
            {emptyState ?? 'No rows found.'}
          </div>
        ) : enableVirtualization ? (
          // Vertical scroll only — horizontal is owned by the outer overflow-x-auto
          // wrapper so the (above-the-scroller) header and the virtualized body scroll
          // together when wide columns overflow. A bare overflow-auto here would give
          // the body its own horizontal scrollbar, desyncing it from the header.
          <div
            ref={scrollParentRef}
            className="overflow-y-auto overflow-x-hidden"
            style={{ maxHeight: toCssSize(maxBodyHeight) }}
          >
            <div role="rowgroup" style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                return (
                  <div
                    key={row.id}
                    role="row"
                    className={cn('absolute left-0 w-full', bodyRowClasses(row))}
                    // NOTE: virtual rows are absolutely positioned, which prevents
                    // `position: sticky` frozen columns from working in this path —
                    // use the non-virtualized path when the table needs frozen
                    // columns (see DataTableProps.enableVirtualization).
                    style={{
                      gridTemplateColumns,
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    data-testid={rowTestId?.(row.original)}
                    {...rowInteractionProps(row, virtualRow.index)}
                  >
                    {renderCells(row)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div role="rowgroup">
            {rows.map((row, rowIndex) => (
              <React.Fragment key={row.id}>
                <div role="row" className={bodyRowClasses(row)} style={{ gridTemplateColumns }} data-testid={rowTestId?.(row.original)} {...rowInteractionProps(row, rowIndex)}>
                  {renderCells(row)}
                </div>
                {row.getIsExpanded() && row.getCanExpand() && renderExpandedRow ? (
                  <div role="row" className="border-t bg-muted/20">
                    <div role="cell" className="px-5 py-4" style={{ gridColumn: `1 / span ${totalColumns}` }}>
                      {renderExpandedRow(row)}
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            ))}
          </div>
        )}
          </div>
        </div>
      </div>

      {showPager ? (
        <DataTablePager
          table={table}
          totalItems={totalItems}
          pageSizeOptions={pageSizeOptions}
          showPageSizeSelector={showPageSizeSelector}
          itemLabel={itemLabel}
        />
      ) : null}
    </div>
  );
}
